export const BUSINESS_CATEGORIES = [
  'Retail',
  'F&B / Hospitality',
  'Real Estate',
  'Automotive',
  'Healthcare',
  'Education',
  'Entertainment & Media',
  'Technology',
  'Banking & Finance',
  'FMCG',
  'Travel & Tourism',
  'Other',
] as const;

export type BusinessCategory = (typeof BUSINESS_CATEGORIES)[number];

/** Sub-categories keyed by Category of Business */
export const BUSINESS_SUB_CATEGORIES: Record<BusinessCategory, string[]> = {
  Retail: [
    'Fashion & Apparel',
    'Electronics',
    'Grocery / Supermarket',
    'Jewellery',
    'Home & Lifestyle',
    'Other Retail',
  ],
  'F&B / Hospitality': [
    'Restaurant',
    'Cafe / QSR',
    'Cloud Kitchen',
    'Hotel / Resort',
    'Catering',
    'Other F&B',
  ],
  'Real Estate': [
    'Residential',
    'Commercial',
    'Co-working',
    'Brokerage',
    'Developer',
    'Other Real Estate',
  ],
  Automotive: [
    'Car Dealership',
    'Two-Wheeler',
    'EV / Mobility',
    'Auto Services',
    'Spare Parts',
    'Other Automotive',
  ],
  Healthcare: [
    'Hospital / Clinic',
    'Diagnostics',
    'Pharmacy',
    'Wellness / Fitness',
    'Dental',
    'Other Healthcare',
  ],
  Education: [
    'K-12 School',
    'College / University',
    'Coaching / EdTech',
    'Skill Training',
    'Preschool',
    'Other Education',
  ],
  'Entertainment & Media': [
    'OTT / Streaming',
    'Events',
    'Gaming',
    'Publishing',
    'Production House',
    'Other Media',
  ],
  Technology: [
    'SaaS / Software',
    'IT Services',
    'Hardware',
    'Telecom',
    'AI / Data',
    'Other Technology',
  ],
  'Banking & Finance': [
    'Banking',
    'Insurance',
    'Fintech',
    'Investments / Wealth',
    'NBFC / Lending',
    'Other Finance',
  ],
  FMCG: [
    'Food & Beverages',
    'Personal Care',
    'Household',
    'Packaged Goods',
    'Other FMCG',
  ],
  'Travel & Tourism': [
    'Airlines',
    'Hotels / Stays',
    'Travel Agency',
    'Tour Operator',
    'Other Travel',
  ],
  Other: ['General', 'Services', 'Manufacturing', 'Consulting', 'Other'],
};

export function getBusinessSubCategories(category?: string): string[] {
  if (!category) return [];
  return BUSINESS_SUB_CATEGORIES[category as BusinessCategory] || ['Other'];
}

export type AddClientFormValues = {
  clientName: string;
  contactPersonName: string;
  mobileNumber: string;
  email: string;
  businessCategory: string;
  businessSubCategory: string;
  address: string;
  pin: string;
};

/**
 * Create-client email only. Empty is allowed (field is optional).
 * TLD must be letters so values like user@111.222.333.444 are rejected.
 */
export const CREATE_CLIENT_EMAIL_REGEX =
  /^[A-Za-z0-9.!#$%&'*+\/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

export function isValidCreateClientEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return CREATE_CLIENT_EMAIL_REGEX.test(trimmed);
}

export const AUTHORIZATION_LETTER_MAX_BYTES = 10 * 1024 * 1024;

export function isPdfFile(file: File) {
  const name = file.name.toLowerCase();
  return file.type === 'application/pdf' || name.endsWith('.pdf');
}
